import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  UNDERWAY_DELAY_MS,
  type UnderwayActivity,
  type UnderwayActivityDetails,
  type UnderwayCandidate,
  type UnderwayStatus,
  type UnderwayViewMode,
} from './types';

export const buildGroupKey = (stageId: string, activityKey: string) =>
  `${stageId}:${activityKey}`;

type EnqueueInput = {
  groupKey: string;
  activityKind: string;
  activityLabel: string;
  stageId: string;
  stageName: string;
  jobId: string;
  jobName: string;
  candidates: UnderwayCandidate[];
  isBulk: boolean;
  executionIds: number[];
  details?: UnderwayActivityDetails;
  // Move-initiation time. Bulk callers pass their dispatch session's
  // `startedAt`; immediate callers omit it and get "now" at enqueue.
  initiatedAt?: number;
  // BE-delayed first step → "scheduled" / "Sends in X" copy (no timer effect).
  delayed?: boolean;
  delaySeconds?: number;
};

// Active (still-running) statuses — these sort ABOVE the terminal ones so the
// latest running action is always pinned to the top of the popup, with
// completed / failed / cancelled rows sinking below.
const ACTIVE_STATUSES: ReadonlySet<UnderwayStatus> = new Set([
  'pending',
  'paused',
  'running',
]);
const isActive = (a: UnderwayActivity): boolean =>
  ACTIVE_STATUSES.has(a.status);

// Recency key for ordering. A row that has actually started running sorts by
// WHEN it started (`startedRunningAt`) so the latest running action floats to
// the top; a row that hasn't run yet falls back to when its move was INITIATED
// (`initiatedAt`) — this also keeps an async bulk row, enqueued only once its
// poller resolves, in move order rather than resolution order. `Array#sort` is
// stable, so equal timestamps keep insertion order (the immediate prepend).
const recencyKey = (a: UnderwayActivity): number =>
  a.startedRunningAt ?? a.initiatedAt ?? a.createdAt;

// Two-level order: running rows first, then terminal — each group most-recent
// first. Keeps the latest running action at the very top while completed items
// settle underneath.
const sortUnderway = (activities: UnderwayActivity[]): UnderwayActivity[] =>
  [...activities].sort((a, b) => {
    const activeDiff = Number(isActive(b)) - Number(isActive(a));
    if (activeDiff !== 0) return activeDiff;
    return recencyKey(b) - recencyKey(a);
  });

type UnderwayState = {
  /** Running rows first (newest first), then terminal — rendered as-is. */
  activities: UnderwayActivity[];
  viewMode: UnderwayViewMode;
  /**
   * Pointer is anywhere over the popup — pauses ALL pending rows. Not
   * persisted, so a refresh mid-hover can't stick a pause.
   */
  isWidgetHovered: boolean;
  setWidgetHovered: (hovered: boolean) => void;
  // Mutations
  enqueue: (input: EnqueueInput) => void;
  pause: (id: string) => void;
  resume: (id: string) => void;
  setStatus: (
    id: string,
    status: UnderwayStatus,
    errorMessage?: string
  ) => void;
  dismiss: (id: string) => void;
  clearTerminal: () => void;
  // View mode
  setViewMode: (mode: UnderwayViewMode) => void;
};

const mergeIntoExisting = (
  existing: UnderwayActivity,
  next: EnqueueInput
): UnderwayActivity => {
  const seenCandidateIds = new Set(existing.candidates.map((c) => c.id));
  const mergedCandidates = [
    ...existing.candidates,
    ...next.candidates.filter((c) => !seenCandidateIds.has(c.id)),
  ];
  const seenExecIds = new Set(existing.executionIds);
  const mergedExecIds = [...existing.executionIds];
  next.executionIds.forEach((id) => {
    if (!seenExecIds.has(id)) {
      mergedExecIds.push(id);
      seenExecIds.add(id);
    }
  });
  return {
    ...existing,
    candidates: mergedCandidates,
    isBulk: existing.isBulk || next.isBulk,
    executionIds: mergedExecIds,
    createdAt: Date.now(),
    delayMs: UNDERWAY_DELAY_MS,
    remainingMs: UNDERWAY_DELAY_MS,
    pausedAt: null,
    status: 'pending',
    // Re-triggered → back to pending, so it hasn't started running this cycle.
    startedRunningAt: undefined,
    errorMessage: undefined,
  };
};

// Persisted to sessionStorage so a refresh during the 15s window
// recovers the popup and UnderwayTimerManager re-fires the still-pending
// executions (see `merge` below — recovered rows are normalised back to
// `pending` with elapsed-adjusted timing). Tab-close / crash are caught
// by the BE sweeper (`Workflow::StageAutomationSweepWorker`).
export const useUnderwayStore = create<UnderwayState>()(
  persist(
    (set) => ({
      activities: [],
      viewMode: 'expanded',
      isWidgetHovered: false,

      setWidgetHovered: (hovered) =>
        set((state) => ({ ...state, isWidgetHovered: hovered })),

      enqueue: (input) =>
        set((state) => {
          const initiatedAt = input.initiatedAt ?? Date.now();
          const existing = state.activities.find(
            (a) => a.id === input.groupKey
          );
          let next: UnderwayActivity[];
          if (existing) {
            // Merging restarts the undo window and re-stamps the initiation
            // time, so a re-triggered group hoists back to the top.
            const merged = {
              ...mergeIntoExisting(existing, input),
              initiatedAt,
            };
            next = [
              merged,
              ...state.activities.filter((a) => a.id !== input.groupKey),
            ];
          } else {
            const fresh: UnderwayActivity = {
              id: input.groupKey,
              activityKind: input.activityKind,
              activityLabel: input.activityLabel,
              stageId: input.stageId,
              stageName: input.stageName,
              jobId: input.jobId,
              jobName: input.jobName,
              candidates: input.candidates,
              isBulk: input.isBulk,
              executionIds: input.executionIds,
              initiatedAt,
              createdAt: Date.now(),
              delayMs: UNDERWAY_DELAY_MS,
              remainingMs: UNDERWAY_DELAY_MS,
              pausedAt: null,
              status: 'pending',
              delayed: input.delayed,
              delaySeconds: input.delaySeconds,
              // Per-kind copy — interview schedule mode, survey audience,
              // reviewer recipients. Without this the "using <mode>" / recipient
              // clauses silently drop from the row (this object lists fields
              // explicitly, so an omission is invisible).
              details: input.details,
            };
            next = [fresh, ...state.activities];
          }
          return {
            ...state,
            activities: sortUnderway(next),
            viewMode: state.viewMode === 'hidden' ? 'expanded' : state.viewMode,
          };
        }),

      pause: (id) =>
        set((state) => ({
          ...state,
          activities: state.activities.map((a) => {
            if (a.id !== id || a.status !== 'pending') return a;
            const elapsed = Date.now() - a.createdAt;
            const remaining = Math.max(0, a.delayMs - elapsed);
            return {
              ...a,
              status: 'paused',
              remainingMs: remaining,
              pausedAt: Date.now(),
            };
          }),
        })),

      resume: (id) =>
        set((state) => ({
          ...state,
          activities: state.activities.map((a) => {
            if (a.id !== id || a.status !== 'paused') return a;
            return {
              ...a,
              status: 'pending',
              createdAt: Date.now() - (a.delayMs - a.remainingMs),
              pausedAt: null,
            };
          }),
        })),

      setStatus: (id, status, errorMessage) =>
        set((state) => ({
          ...state,
          // Re-sort: stamping `startedRunningAt` when a row starts running
          // floats it to the top of the active group; a row moving to a terminal
          // status (completed / failed / cancelled) sinks below the running ones.
          activities: sortUnderway(
            state.activities.map((a) => {
              if (a.id !== id) return a;
              const startedRunningAt =
                status === 'running' && a.status !== 'running'
                  ? Date.now()
                  : a.startedRunningAt;
              return { ...a, status, errorMessage, startedRunningAt };
            })
          ),
        })),

      dismiss: (id) =>
        set((state) => ({
          ...state,
          activities: state.activities.filter((a) => a.id !== id),
        })),

      clearTerminal: () =>
        set((state) => ({
          ...state,
          activities: state.activities.filter(
            (a) =>
              a.status === 'pending' ||
              a.status === 'paused' ||
              a.status === 'running'
          ),
        })),

      setViewMode: (mode) => set((state) => ({ ...state, viewMode: mode })),
    }),
    {
      name: 'kula-underway-popup',
      version: 1,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        activities: state.activities.filter(
          (a) =>
            a.status === 'pending' ||
            a.status === 'paused' ||
            a.status === 'running'
        ),
        viewMode: state.viewMode,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<
          Pick<UnderwayState, 'activities' | 'viewMode'>
        >;
        const now = Date.now();
        const activities = (p.activities ?? []).map((a) => {
          let createdAt = a.createdAt;
          if (a.status === 'paused') {
            createdAt = now - (a.delayMs - a.remainingMs);
          } else if (a.status === 'running') {
            createdAt = now - a.delayMs;
          }
          return {
            ...a,
            // Preserve the original move-initiation time across a refresh
            // (fallback to the persisted createdAt for pre-initiatedAt rows).
            initiatedAt: a.initiatedAt ?? a.createdAt,
            status: 'pending' as const,
            createdAt,
            // Recovered rows are re-fired from pending, so drop any prior
            // start-running time — they'll re-stamp when they run again.
            startedRunningAt: undefined,
            remainingMs: a.delayMs,
            pausedAt: null,
            errorMessage: undefined,
          };
        });
        return {
          ...current,
          activities: sortUnderway(activities),
          viewMode: p.viewMode ?? current.viewMode,
        };
      },
    }
  )
);
